import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { Avatar } from '../../components';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { useUIStore } from '../../stores';
import { cn } from '../../lib/utils';

export default function Topbar() {
  const { user, logout } = useAuth();
  const { toggleSidebar, soundEnabled, toggleSound } = useUIStore();

  const iconBtn = 'rounded-full p-2 text-ink-2 transition-colors hover:bg-black/[0.05] hover:text-ink';

  return (
    <header className="flex h-16 items-center justify-between border-b border-line bg-surface px-4 sm:px-6">
      {/* Left side */}
      <div className="flex items-center gap-4">
        <button onClick={toggleSidebar} className={iconBtn}>
          <Bars3Icon className="h-6 w-6" />
        </button>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* Sound toggle */}
        <button
          onClick={toggleSound}
          className={iconBtn}
          title={soundEnabled ? 'Mute notifications' : 'Unmute notifications'}
        >
          {soundEnabled ? (
            <SpeakerWaveIcon className="h-5 w-5" />
          ) : (
            <SpeakerXMarkIcon className="h-5 w-5" />
          )}
        </button>

        {/* Notifications */}
        <Menu as="div" className="relative">
          <Menu.Button className={cn(iconBtn, 'relative')} aria-label="Notifications">
            <BellIcon className="h-6 w-6" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-80 origin-top-right divide-y divide-line rounded-card bg-surface shadow-l ring-1 ring-line focus:outline-none">
              <div className="px-4 py-3">
                <p className="text-sm font-medium text-ink">Notifications</p>
                <p className="mt-1 text-xs text-ink-3">
                  You're all caught up — nothing new right now.
                </p>
              </div>
              <div className="p-1">
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/settings"
                      className={cn(
                        'group flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-ink-2',
                        active && 'bg-black/[0.05] text-ink'
                      )}
                    >
                      <Cog6ToothIcon className="h-5 w-5 text-ink-3" />
                      Notification settings
                    </Link>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>

        {/* User Menu */}
        <Menu as="div" className="relative">
          <Menu.Button className="flex items-center gap-3 rounded-full px-2 py-1.5 transition-colors hover:bg-black/[0.05]">
            <Avatar src={user?.avatar} name={user?.name} size="sm" online={user?.online} />
            <div className="hidden text-left sm:block">
              <p className="text-sm font-medium text-ink">{user?.name}</p>
              <p className="text-xs capitalize text-ink-2">{user?.role}</p>
            </div>
            <ChevronDownIcon className="hidden h-4 w-4 text-ink-3 sm:block" />
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-line rounded-card bg-surface shadow-l ring-1 ring-line focus:outline-none">
              <div className="p-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={cn(
                        'group flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-ink-2',
                        active && 'bg-black/[0.05] text-ink'
                      )}
                    >
                      <UserCircleIcon className="h-5 w-5 text-ink-3" />
                      Profile Settings
                    </button>
                  )}
                </Menu.Item>
              </div>

              <div className="p-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => logout()}
                      className={cn(
                        'group flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-danger',
                        active && 'bg-danger/10'
                      )}
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5" />
                      Sign Out
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </header>
  );
}
